import {
  getBlockDisplayData,
  validateBlockNesting,
} from '@basehub/data/replicache/mutators/repo-space/block/helpers/component-helpers'
import {
  blockTypesRecord,
  isComponentBlock,
} from '@basehub/data/replicache/index'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { getNewBlockDataByType } from '@basehub/data/replicache/helpers/get-new-block-data-by-type'
import { getId } from '@basehub/utils/id/index'

import type { Block, ComponentBlock } from '@basehub/data/replicache/index'
import { useQuery } from '@tanstack/react-query'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'

export const useValidInstancesForBlock = ({
  blockId,
  dependencies,
  disableHiddenListTemplates,
}: {
  blockId: Block['id']
  dependencies?: React.DependencyList | null
  disableHiddenListTemplates?: boolean
}) => {
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const getChildrenKeys = useWorkingTreeStore((s) => s.tree?.getChildrenKeys)
  const getPath = useWorkingTreeStore((s) => s.tree?.getPath)
  const componentsSet = useWorkingTreeStore(
    (s) =>
      (s.tree?.compoundIndexes.get('components') ?? new Set()) as Set<string>
  )

  return useQuery(
    ['valid-instances-for-block', blockId, dependencies, componentsSet],
    async () => {
      const allComponents: ComponentBlock[] = []
      const validInstances: ComponentBlock[] = []
      const block = getNode?.(blockId)?.value
      const blockPath = getPath?.(blockId, { format: 'string' })

      if (!getNode || !blockPath) {
        return { validInstances, allComponents }
      }

      const newBlockId = getId()
      for (const compId of componentsSet.values()) {
        const treeNode = getNode(compId)
        if (!treeNode) continue
        if (!isComponentBlock(treeNode.value) || !treeNode.parentKey) continue

        const parent = getNode(treeNode.parentKey, { required: true })
        const displayData = getBlockDisplayData({
          block: treeNode.value,
          parentBlock: parent.value,
        })

        if (disableHiddenListTemplates && treeNode.value.hidden) {
          // don't show hidden list templates if disableHiddenListTemplates is true
          const parent = treeNode.parentKey ? getNode(treeNode.parentKey) : null
          if (parent?.value.type === blockTypesRecord.list) continue
        }

        // push to allComponents
        allComponents.push({
          ...treeNode.value,
          title: displayData.displayTitle,
        })

        // then check if it's nestable / valid

        if (
          parent.value &&
          (parent.value.type === 'component' ||
            parent.value.type === 'reference' ||
            parent.value.type === 'rich-text') &&
          treeNode.parentKey === parent.key
        ) {
          // is internal block
          if (
            (block && block.id === parent.value.id) ||
            blockPath.includes(parent.value.id)
          ) {
            // internal to this ref, cool!
          } else {
            continue
          }
        }

        const blockSpecificStuff = getNewBlockDataByType({
          id: newBlockId,
          type: blockTypesRecord.instance,
          typeOptions: {
            // Use sourceBlockId to ensure consistent comparison in validateBlockNesting,
            // which uses idToSourceBlockId for CoW scenarios in branches
            targetBlockId: idToSourceBlockId(treeNode.value),
          },
        })
        const newBlock: Block = {
          ...blockSpecificStuff,
          id: newBlockId,
          title: '',
          hidden: false,
          createdAt: new Date().toISOString(),
          isCommitted: false,
          sourceBlockId: null,
        }

        const { isValid } = validateBlockNesting({
          block: newBlock,
          path: blockPath,
          shouldThrowIfInvalid: false,
          fetchBlock: (id) => getNode(id)?.value,
          fetchChildren: (id) => {
            const keys = getChildrenKeys?.(id)

            return keys?.map((k) => getNode(k)?.value).filter(Boolean) ?? []
          },
        })
        if (!isValid) continue

        validInstances.push({
          ...treeNode.value,
          title: treeNode.value.title || 'Untitled',
        })
      }

      return { validInstances, allComponents }
    }
  )
}
